import { useEffect, useRef } from "react";

/**
 * Reusable a11y plumbing for every modal in the app. Once attached:
 *
 *   1. **Escape closes** — pressing Escape while focus is anywhere
 *      inside the document calls `onClose`. Some modals already had
 *      ad-hoc Escape handling; this gives every modal the same one.
 *
 *   2. **Focus trap** — Tab / Shift+Tab cycle stays inside the modal.
 *      Without this, Tab leaks to the page underneath (sidebar links,
 *      the breadcrumb, etc.) and the modal silently loses focus.
 *
 *   3. **Return focus on close** — when the modal opens we stash
 *      `document.activeElement`; when it unmounts (or `open` flips to
 *      false) focus is restored to that trigger. Critical for keyboard
 *      / screen-reader users who would otherwise land at the document
 *      root after dismissing the modal.
 *
 *   4. **Initial focus** — moves focus to the first focusable element
 *      inside the modal on open, so the user can immediately interact
 *      with the modal content without an extra Tab.
 *
 * Usage:
 *
 *     const ref = useRef(null);
 *     useModalA11y({ open, onClose, containerRef: ref });
 *     return open ? <div ref={ref} role="dialog" aria-modal="true">...</div> : null;
 *
 * The hook is a no-op when `open` is false, so calling it
 * unconditionally from a component body is safe.
 */
export function useModalA11y({ open, onClose, containerRef, initialFocusRef = null }) {
    // Track the element that had focus BEFORE the modal opened so we can
    // restore it on close. Stored on the ref instead of useState because
    // we don't want a re-render when it changes.
    const lastFocusedRef = useRef(null);

    // Keep the latest onClose in a ref so the focus effect below does NOT
    // list it as a dependency. Callers almost always pass a fresh inline
    // arrow on every render (e.g. `() => !busy && onClose?.()`), and some
    // modals live under pages that re-render on a 1-second ticker. If the
    // effect depended on onClose it would re-run every render, and its
    // initial-focus setTimeout would yank focus back to the first
    // focusable element (the header close button) every tick — stealing
    // focus out of any input the user is typing into.
    const onCloseRef = useRef(onClose);
    useEffect(() => {
        onCloseRef.current = onClose;
    }, [onClose]);

    useEffect(() => {
        if (!open) return undefined;

        // Stash the previously focused element. Defaults to document.body
        // if nothing was focused, which restoreFocus handles safely.
        lastFocusedRef.current = document.activeElement;

        // Defer initial focus to the next tick so the modal has actually
        // mounted and child refs (initialFocusRef) are populated.
        const focusTimer = window.setTimeout(() => {
            const explicit = initialFocusRef?.current;
            if (explicit && typeof explicit.focus === "function") {
                explicit.focus();
                return;
            }
            const first = firstFocusable(containerRef?.current);
            if (first) first.focus();
        }, 0);

        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                event.stopPropagation();
                onCloseRef.current?.();
                return;
            }
            if (event.key === "Tab") {
                trapTab(event, containerRef?.current);
            }
        };

        document.addEventListener("keydown", onKeyDown, true);
        return () => {
            window.clearTimeout(focusTimer);
            document.removeEventListener("keydown", onKeyDown, true);
            // Restore focus to the trigger that opened the modal. Wrapped
            // in a try/catch because the element may have been removed
            // from the DOM while the modal was open (e.g. row deleted
            // after a confirm).
            const target = lastFocusedRef.current;
            if (target && typeof target.focus === "function") {
                try {
                    target.focus();
                } catch {
                    /* element gone — drop focus on the body */
                }
            }
        };
    }, [open, containerRef, initialFocusRef]);
}

/**
 * Returns the first keyboard-focusable element inside `root`, or null
 * if there isn't one. Mirrors the WAI-ARIA Authoring Practices guide
 * for dialog initial focus.
 */
function firstFocusable(root) {
    if (!root) return null;
    const all = root.querySelectorAll(FOCUSABLE_SELECTOR);
    for (const el of all) {
        if (isVisible(el)) return el;
    }
    return null;
}

function trapTab(event, root) {
    if (!root) return;
    const nodes = Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR)).filter(isVisible);
    if (nodes.length === 0) {
        event.preventDefault();
        return;
    }
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    const active = document.activeElement;
    if (event.shiftKey) {
        if (active === first || !root.contains(active)) {
            event.preventDefault();
            last.focus();
        }
    } else if (active === last) {
        event.preventDefault();
        first.focus();
    }
}

/**
 * Visibility check used by the focus trap — an element with `display:none`
 * or zero size shouldn't be in the tab cycle even if it matches the
 * selector. We keep this simple (offsetParent !== null) since the modal
 * content is always rendered on top with non-zero dimensions.
 */
function isVisible(el) {
    if (!el) return false;
    if (el.hasAttribute("disabled")) return false;
    if (el.getAttribute("aria-hidden") === "true") return false;
    return el.offsetParent !== null || el === document.activeElement;
}

const FOCUSABLE_SELECTOR = [
    "a[href]",
    "area[href]",
    "button:not([disabled])",
    "input:not([disabled]):not([type='hidden'])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "iframe",
    "object",
    "embed",
    "[contenteditable='true']",
    "[tabindex]:not([tabindex='-1'])"
].join(",");
